// src/components/kuestion/shared/AccountMenu.jsx
//
// The account menu (Preferences & Timezone build brief §12): opened from the
// account name in the header. Timezone, back to detected, clock, theme,
// show me around again, log out.
//
// Zones are IANA ids, never offsets: offsets move twice a year, zones do not.
// The picker offers city zones only. No Etc/*: the sign is inverted there.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usePreferences } from './PreferencesContext';
import { useTour } from './Tour';
import { CURATED_ZONES, zoneLabelFor, zoneOffset, clockIn, isValidZone } from './display';
import { SunIcon, MoonIcon, LogoutIcon } from './Icons';
import { DOWNLOADS_PATH } from '../../../config/downloads';

const Chevron = () => (
  <svg className="kqi" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9.5 12 15.5 18 9.5" /></svg>
);
const HelpIcon = () => (
  <svg className="kqi" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9.2" /><path d="M9.4 9.1a2.7 2.7 0 1 1 3.4 2.6c-.5.2-.8.6-.8 1.1v.5" /><line x1="12" y1="16.4" x2="12" y2="16.4" /></svg>
);
const SystemIcon = () => (
  <svg className="kqi" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="12" /><path d="M8 20h8M12 16v4" /></svg>
);

export default function AccountMenu({ name, role, initial, onLogout, note }) {
  const p = usePreferences();
  const tour = useTour();
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const ref = useRef(null);

  // the clock line ticks while the menu is open
  useEffect(() => {
    if (!open) return undefined;
    setNow(new Date());
    const t = setInterval(() => setNow(new Date()), 15000);
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => { clearInterval(t); window.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); };
  }, [open]);

  const zone = p ? p.zone : 'UTC';
  const device = p ? p.deviceZone : null;
  const explicit = Boolean(p && p.prefs.timezone);
  const onDevice = Boolean(device) && zone === device;

  // The effective zone leads the list, then the device zone, then the curated
  // ones. Anything the server holds that we do not list is still shown.
  const zones = useMemo(() => {
    const seen = new Set();
    const out = [];
    // Etc/* is never offered, but a zone the account already holds is always shown.
    [zone, device, ...CURATED_ZONES].forEach((id) => { if (id && !seen.has(id) && isValidZone(id) && (id === zone || !id.startsWith('Etc/'))) { seen.add(id); out.push(id); } });
    return out;
  }, [zone, device]);

  const clock = p ? p.prefs.clock : '24';
  const theme = p ? p.prefs.theme : 'system';

  return (
    <div className="kqc-account" ref={ref}>
      <button type="button" className="kqc-account__btn" onClick={() => setOpen((o) => !o)} title="Account and preferences" aria-haspopup="true" aria-expanded={open}>
        <span className="kqc-avatar">{initial}</span>
        <span className="kqc-account__who">
          <strong>{name}</strong>
          <em>{role}</em>
        </span>
        <Chevron />
      </button>

      {open && (
        <div className="kqc-account__menu" role="menu" aria-label="Account and preferences">
          <div className="kqc-account__head">
            <div className="kqc-kicker">Preferences</div>
            <div className="kqc-account__sub">Times &amp; display</div>
          </div>

          <div className="kqc-account__section">
            <label className="kqc-account__label" htmlFor="kq-tz">Timezone</label>
            <select id="kq-tz" className="input" value={zone} onChange={(e) => p && p.setTimezone(e.target.value)} disabled={!p}>
              {zones.map((id) => <option key={id} value={id}>{zoneLabelFor(id)}</option>)}
            </select>
            <div className="kqc-account__now">
              <span>Now <strong>{clockIn(now, zone, clock)}</strong> · {zoneOffset(zone, now)}</span>
              {onDevice && <span className="tag tag-neutral">Detected from your browser</span>}
            </div>
            {/* only when the choice actually differs from the device: otherwise the tag above already says it */}
            {explicit && device && !onDevice && (
              <button type="button" className="kqc-account__link" onClick={() => p.setTimezone(null)}>
                Back to detected &mdash; {zoneLabelFor(device)}
              </button>
            )}
            <p className="kqc-small kqc-muted" style={{ margin: '8px 0 0' }}>{note || 'Windows you set are stored in UTC. Each person sees them in their own zone, named.'}</p>
            {p && p.source === 'local' && <p className="kqc-small kqc-muted" style={{ margin: '6px 0 0' }}>Saved on this device for now.</p>}
          </div>

          <div className="kqc-account__section kqc-account__row">
            <span className="kqc-account__label">Clock</span>
            <div className="kqc-seg" role="group" aria-label="Clock">
              <button type="button" className={clock === '24' ? 'is-on' : ''} aria-pressed={clock === '24'} onClick={() => p && p.setClock('24')}>24h</button>
              <button type="button" className={clock === '12' ? 'is-on' : ''} aria-pressed={clock === '12'} onClick={() => p && p.setClock('12')}>12h</button>
            </div>
          </div>

          {/* Three-way, so "system" is a state the user can get back to (§12.1). */}
          <div className="kqc-account__section kqc-account__row">
            <span className="kqc-account__label">Theme</span>
            <div className="kqc-seg" role="group" aria-label="Theme">
              <button type="button" className={theme === 'light' ? 'is-on' : ''} aria-pressed={theme === 'light'} onClick={() => p && p.setTheme('light')} title="Light"><SunIcon />Light</button>
              <button type="button" className={theme === 'dark' ? 'is-on' : ''} aria-pressed={theme === 'dark'} onClick={() => p && p.setTheme('dark')} title="Dark"><MoonIcon />Dark</button>
              <button type="button" className={theme === 'system' ? 'is-on' : ''} aria-pressed={theme === 'system'} onClick={() => p && p.setTheme('system')} title="Follow the system"><SystemIcon />System</button>
            </div>
          </div>

          {tour && (
            <button type="button" className="kqc-account__item" role="menuitem" onClick={() => { setOpen(false); tour.startTour(); }}>
              <HelpIcon />Show me around again
            </button>
          )}
          {/* Opens the Downloads page in a new tab: nobody loses the screen they were on. */}
          <a className="kqc-account__item" role="menuitem" href={DOWNLOADS_PATH} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>
            <svg className="kqi" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="6" y="2.5" width="12" height="19" /><line x1="11" y1="18" x2="13" y2="18" /></svg>
            <span style={{ flex: 1 }}>Get the Android app</span>
            <svg className="kqi kqc-account__ext" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 4h6v6" /><line x1="20" y1="4" x2="11" y2="13" /><path d="M18 14.5V19a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 19V7.5A1.5 1.5 0 0 1 5 6h4.5" /></svg>
          </a>
          <button type="button" className="kqc-account__item kqc-account__item--out" role="menuitem" onClick={onLogout}>
            <LogoutIcon />Log out
          </button>
        </div>
      )}
    </div>
  );
}
